import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  Plus, X, PencilSimple, Trash, CheckCircle, ArrowRight,
  Check, ClipboardText,
} from "@phosphor-icons/react";
import Thumb from "@/components/Thumb";
import SearchBar, { textMatch } from "@/components/SearchBar";
import { fetchPrimaryAttachments, bulkPrimaryMap } from "@/lib/attachments";

/* ─────────────── Shared UI ─────────────── */
const inputCls = "w-full bg-transparent border-b py-2 outline-none focus:border-[#C2A878] text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="data-label mb-2">{label}</div>
      {children}
    </label>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-10" data-testid="modal-overlay"
      style={{ background: "rgba(26,26,26,0.4)" }} onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-4xl" : "max-w-2xl"} mx-4 p-8 max-h-[92vh] overflow-y-auto`}
        style={{ background: "var(--nylsu-card)", border: "1px solid var(--nylsu-border)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-serif text-2xl tracking-tight">{title}</h2>
          <button onClick={onClose} data-testid="modal-close-btn" className="text-[#8F8D84] hover:text-[#1A1A1A]">
            <X size={18} weight="light" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
      <div>
        <div className="overline mb-3">{eyebrow}</div>
        <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-none">{title}</h1>
        {subtitle && <p className="mt-4 text-sm text-[#5C5A54] max-w-xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

function SectionCard({ children, testId }) {
  return (
    <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid={testId}>
      {children}
    </div>
  );
}

function EmptyRow({ colSpan, text }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12 text-sm text-[#8F8D84] text-center">{text}</td>
    </tr>
  );
}

function PrimaryBtn({ children, testId, disabled, type = "submit", onClick, className = "" }) {
  return (
    <button type={type} onClick={onClick} data-testid={testId} disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60 ${className}`}
      style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
      {children}
    </button>
  );
}

function GhostBtn({ children, testId, onClick, className = "" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors hover:border-[#1A1A1A] ${className}`}
      style={{ borderColor: "var(--nylsu-border-strong)", color: "#1A1A1A" }}>
      {children}
    </button>
  );
}

function StatusPill({ status, tone = "gold", testId }) {
  const map = {
    gold: "var(--nylsu-gold)", warn: "var(--nylsu-warning)",
    danger: "var(--nylsu-danger)", success: "var(--nylsu-success)",
    muted: "#8F8D84",
  };
  return (
    <span data-testid={testId} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border"
      style={{ borderColor: map[tone], color: map[tone] }}>
      {status}
    </span>
  );
}

function useList(url, deps = []) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    if (!url) return;
    setLoading(true);
    try { setRows((await api.get(url)).data); }
    catch (e) {
      if (![403, 404].includes(e?.response?.status)) toast.error(formatApiError(e));
      setRows([]);
    }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, deps);
  return [rows, load, loading];
}

/* ─────────────── Meta helpers ─────────────── */
function orderStatusTone(s) {
  return {
    draft: "muted", confirmed: "gold", inventory_check: "gold",
    reserved: "success", production_required: "warn",
    in_production: "warn", partially_ready: "gold",
    ready: "success", packed: "success", delivered: "success",
    cancelled: "danger",
  }[s] || "muted";
}

function poStatusTone(s) {
  return {
    draft: "muted", material_check: "gold", material_shortage: "danger",
    ready_to_start: "success", in_production: "warn", qc: "gold",
    completed: "success", on_hold: "warn", cancelled: "danger",
  }[s] || "muted";
}

function fmtDaysBadge(days, t) {
  if (days == null) return null;
  if (days < 0) return <StatusPill status={t("phase2.late") + " · " + Math.abs(days) + "d"} tone="danger" />;
  if (days <= 3) return <StatusPill status={days + "d · " + t("phase2.atRisk")} tone="warn" />;
  return <StatusPill status={days + "d"} tone="success" />;
}

/* ═══════════════════════════════════════════════════════════════════
   CUSTOMERS
═══════════════════════════════════════════════════════════════════ */
const CUSTOMER_TYPES = ["retail", "showroom", "wholesale", "mto", "vip", "other"];

function CustomerForm({ initial, onSaved }) {
  const { t, tEnum } = useI18n();
  const [form, setForm] = useState({
    name: "", type: "retail", email: "", phone: "", country: "TR",
    city: "", company: "", tax_id: "", address: "", notes: "",
    vip: false, active: true, ...initial,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial?.id) await api.patch(`/customers/${initial.id}`, form);
      else await api.post("/customers", form);
      toast.success(t(initial?.id ? "customers.updatedToast" : "customers.createdToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="customer-form">
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.name")}>
          <input required data-testid="customer-name-input" className={inputCls}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={t("common.type")}>
          <select data-testid="customer-type-select" className={inputCls} value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {CUSTOMER_TYPES.map((c) => <option key={c} value={c}>{tEnum("customer.type", c)}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.email")}>
          <input type="email" data-testid="customer-email-input" className={inputCls}
            value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={t("common.phone")}>
          <input data-testid="customer-phone-input" className={inputCls}
            value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.country")}>
          <input maxLength={2} className={inputCls} value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} />
        </Field>
        <Field label={t("common.city")}>
          <input className={inputCls} value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </Field>
      </div>
      <Field label={t("customers.company")}>
        <input className={inputCls} value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} />
      </Field>
      <Field label={t("common.notes")}>
        <textarea rows={2} className={inputCls} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" data-testid="customer-vip-checkbox" checked={form.vip}
          onChange={(e) => setForm({ ...form, vip: e.target.checked })} />
        <span>{t("customers.vip")}</span>
      </label>
      <PrimaryBtn testId="customer-submit-btn">{initial?.id ? t("common.save") : t("customers.createCta")}</PrimaryBtn>
    </form>
  );
}

export function CustomersPage() {
  const { can } = useAuth();
  const { t, tEnum } = useI18n();
  const [rows, reload] = useList("/customers");
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const digitsQ = q.replace(/\D+/g, "");
  const filtered = useMemo(() => rows.filter((c) => {
    if (typeFilter && c.type !== typeFilter) return false;
    if (!q.trim()) return true;
    if (textMatch(q, c.name, c.email, c.city, c.country, c.company)) return true;
    // Phone digit-substring match on stored phone or normalized digits
    if (digitsQ.length >= 3) {
      const rawDigits = (c.phone || "").replace(/\D+/g, "");
      const norm = (c.phone_normalized || "");
      if (rawDigits.includes(digitsQ) || norm.includes(digitsQ)) return true;
    }
    return false;
  }), [rows, q, digitsQ, typeFilter]);

  const del = async (row) => {
    if (!window.confirm(t("customers.deleteConfirm", { name: row.name }))) return;
    try { await api.delete(`/customers/${row.id}`); toast.success(t("common.deletedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="customers-page">
      <PageHeader eyebrow={t("customers.eyebrow")} title={t("customers.title")} subtitle={t("customers.subtitle")}
        actions={can("customer:create") && (
          <PrimaryBtn type="button" onClick={() => setModal("new")} testId="new-customer-btn">
            <Plus size={14} /> {t("customers.new")}
          </PrimaryBtn>
        )} />
      <SearchBar
        testId="customers-search"
        value={q}
        onChange={setQ}
        placeholder={t("customers.searchPlaceholder")}
        resultCount={`${filtered.length} / ${rows.length}`}
        filters={
          <>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("common.type")}</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="text-xs bg-transparent border-b py-1 outline-none focus:border-[#C2A878]"
              data-testid="customers-type-filter">
              <option value="">{t("common.all")}</option>
              {["retail", "showroom", "wholesale", "mto", "vip", "other"].map((v) =>
                <option key={v} value={v}>{tEnum("customer.type", v)}</option>)}
            </select>
            {(q || typeFilter) && (
              <button type="button" onClick={() => { setQ(""); setTypeFilter(""); }}
                      className="text-[10px] uppercase tracking-[0.15em] underline text-[#8F8D84]"
                      data-testid="customers-clear-filters">{t("common.clearFilters")}</button>
            )}
          </>
        } />
      <SectionCard testId="customers-table-card">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.type")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.email")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.phone")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.city")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <EmptyRow colSpan={6} text={q || typeFilter ? t("common.noMatches") : t("customers.empty")} />}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}
                data-testid={`customer-row-${c.id}`}>
                <td className="px-6">
                  <div className="flex items-center gap-2">
                    <span className="font-serif text-lg">{c.name}</span>
                    {c.vip && <StatusPill status="VIP" tone="gold" />}
                  </div>
                </td>
                <td className="px-6 text-xs uppercase tracking-[0.1em]">{tEnum("customer.type", c.type)}</td>
                <td className="px-6 text-xs">{c.email || "—"}</td>
                <td className="px-6 text-xs">{c.phone || "—"}</td>
                <td className="px-6 text-xs">{[c.city, c.country].filter(Boolean).join(", ")}</td>
                <td className="px-6">
                  <div className="flex gap-1 justify-end">
                    {can("customer:edit") && (
                      <button onClick={() => setModal({ edit: c })} data-testid={`customer-${c.id}-edit`}
                        className="p-2 text-[#5C5A54] hover:text-[#1A1A1A]"><PencilSimple size={16} weight="light" /></button>
                    )}
                    {can("customer:delete") && (
                      <button onClick={() => del(c)} data-testid={`customer-${c.id}-delete`}
                        className="p-2 text-[#5C5A54] hover:text-[#8C3B3B]"><Trash size={16} weight="light" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
      {modal === "new" && <Modal onClose={() => setModal(null)} title={t("customers.new")}>
        <CustomerForm onSaved={() => { setModal(null); reload(); }} />
      </Modal>}
      {modal?.edit && <Modal onClose={() => setModal(null)} title={t("customers.edit")}>
        <CustomerForm initial={modal.edit} onSaved={() => { setModal(null); reload(); }} />
      </Modal>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ORDERS
═══════════════════════════════════════════════════════════════════ */
const ORDER_SOURCES = ["manual", "showroom", "wholesale", "mto", "other"];
const PRIORITIES = ["normal", "high", "urgent", "vip"];

function NewOrderForm({ mto, onSaved }) {
  const { t, tEnum, localName } = useI18n();
  const { user } = useAuth();
  const canOverridePrice = user?.role === "admin"
    || (user?.permissions || []).includes("order:override_price");
  const [customers] = useList("/customers");
  const [styles] = useList("/styles");
  const [locations] = useList("/locations");
  const [form, setForm] = useState({
    customer_id: "", source: mto ? "mto" : "manual", is_mto: !!mto,
    location_id: "", currency: "EUR", priority: "normal",
    required_delivery_date: "", event_date: "",
    internal_notes: "", customer_notes: "",
    items: [{ style_id: "", sku: "", color: "", size: "", quantity: 1, unit_price: 0, discount: 0 }],
    mto_measurements: { bust: "", waist: "", hip: "", shoulder: "", back_width: "", dress_length: "", sleeve_length: "", height: "" },
    mto_special_instructions: "",
    mto_reference_style_id: "",
  });
  useEffect(() => {
    if (!form.customer_id && customers[0]) setForm((f) => ({ ...f, customer_id: customers[0].id }));
    if (!form.location_id && locations[0]) setForm((f) => ({ ...f, location_id: locations[0].id }));
    // eslint-disable-next-line
  }, [customers, locations]);

  const setItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    // when style changes, keep sku empty; when variant chosen, set sku
    if (k === "style_id") {
      items[i].sku = ""; items[i].color = ""; items[i].size = "";
    }
    setForm({ ...form, items });
  };
  const setVariant = (i, sku) => {
    const items = [...form.items];
    const style = styles.find((s) => s.id === items[i].style_id);
    const v = style?.variants?.find((x) => x.sku === sku);
    // Auto-populate unit_price from Product Master (style.retail_price) — user
    // may still override if they hold the order:override_price permission.
    const master = style?.retail_price ?? items[i].unit_price ?? 0;
    items[i] = {
      ...items[i],
      sku,
      color: v?.color || "",
      size: v?.size || "",
      unit_price: (!items[i].unit_price || Number(items[i].unit_price) === 0)
        ? master
        : items[i].unit_price,
    };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({
    ...form,
    items: [...form.items, { style_id: "", sku: "", color: "", size: "", quantity: 1, unit_price: 0, discount: 0 }],
  });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        items: form.items.filter((it) => it.sku && Number(it.quantity) > 0).map((it) => ({
          ...it,
          quantity: Number(it.quantity), unit_price: Number(it.unit_price),
          discount: Number(it.discount || 0),
        })),
      };
      if (mto) {
        payload.mto_measurements = Object.fromEntries(
          Object.entries(form.mto_measurements).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])
        );
      } else {
        delete payload.mto_measurements;
      }
      if (payload.items.length === 0) { toast.error(t("orders.needAtLeastOneItem")); return; }
      const r = await api.post("/orders", payload);
      toast.success(t("orders.createdToast", { number: r.data.order_number }));
      onSaved(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="new-order-form">
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("orders.customer")}>
          <select required data-testid="order-customer-select" className={inputCls} value={form.customer_id}
            onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
            <option value="">—</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name} {c.vip ? "★" : ""} · {tEnum("customer.type", c.type)}</option>
            ))}
          </select>
        </Field>
        <Field label={t("orders.source")}>
          <select data-testid="order-source-select" className={inputCls} value={form.source} disabled={mto}
            onChange={(e) => setForm({ ...form, source: e.target.value })}>
            {(mto ? ["mto"] : ORDER_SOURCES).map((s) => <option key={s} value={s}>{tEnum("order.source", s)}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-6">
        <Field label={t("common.location")}>
          <select required data-testid="order-location-select" className={inputCls} value={form.location_id}
            onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label={t("common.currency")}>
          <select data-testid="order-currency-select" className={inputCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            {["EUR", "TRY", "USD", "GBP"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label={t("orders.priority")}>
          <select data-testid="order-priority-select" className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{tEnum("priority", p)}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("orders.deliveryDate")}>
          <input type="date" data-testid="order-delivery-date" className={inputCls} value={form.required_delivery_date}
            onChange={(e) => setForm({ ...form, required_delivery_date: e.target.value })} />
        </Field>
        {mto && (
          <Field label={t("orders.eventDate")}>
            <input type="date" data-testid="mto-event-date" className={inputCls} value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
          </Field>
        )}
      </div>

      {mto && (
        <div className="pt-4 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
          <div className="data-label mb-3">{t("mto.measurements")}</div>
          <div className="grid grid-cols-4 gap-4">
            {["bust", "waist", "hip", "shoulder", "back_width", "dress_length", "sleeve_length", "height"].map((k) => (
              <Field key={k} label={t("mto.m." + k)}>
                <input type="number" step="0.1" data-testid={`mto-m-${k}`} className={inputCls}
                  value={form.mto_measurements[k]}
                  onChange={(e) => setForm({ ...form, mto_measurements: { ...form.mto_measurements, [k]: e.target.value } })}
                  placeholder="cm" />
              </Field>
            ))}
          </div>
          <Field label={t("mto.specialInstructions")}>
            <textarea rows={2} data-testid="mto-special-instructions" className={inputCls} value={form.mto_special_instructions}
              onChange={(e) => setForm({ ...form, mto_special_instructions: e.target.value })} />
          </Field>
        </div>
      )}

      <div className="pt-4 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="data-label">{t("orders.items")}</div>
          <button type="button" onClick={addItem} data-testid="order-add-item-btn"
            className="text-xs uppercase tracking-[0.15em] link-underline">+ {t("orders.addItem")}</button>
        </div>
        {form.items.length > 0 && (
          <div className="grid grid-cols-[2fr_2fr_80px_120px_120px_auto] gap-3 mb-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("orders.col.style")}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("orders.col.variant")}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("orders.col.qty")}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("orders.col.unitPrice")}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("orders.col.discount")}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] text-right">{t("orders.col.remove")}</div>
          </div>
        )}
        <div className="space-y-3">
          {form.items.map((it, i) => {
            const style = styles.find((s) => s.id === it.style_id);
            const variants = style?.variants || [];
            return (
              <div key={i} className="grid grid-cols-[2fr_2fr_80px_120px_120px_auto] gap-3 items-end" data-testid={`order-item-row-${i}`}>
                <select required className={inputCls} value={it.style_id} data-testid={`order-item-style-${i}`}
                  onChange={(e) => setItem(i, "style_id", e.target.value)}>
                  <option value="">{t("orders.selectStyle")}</option>
                  {styles.map((s) => <option key={s.id} value={s.id}>{s.style_code} — {s.name}</option>)}
                </select>
                <select required className={inputCls} value={it.sku} data-testid={`order-item-sku-${i}`}
                  onChange={(e) => setVariant(i, e.target.value)}>
                  <option value="">{t("orders.selectVariant")}</option>
                  {variants.map((v) => <option key={v.sku} value={v.sku}>{v.color} / {v.size}</option>)}
                </select>
                <input required type="number" min="1" className={inputCls} value={it.quantity} data-testid={`order-item-qty-${i}`}
                  onChange={(e) => setItem(i, "quantity", e.target.value)} />
                <input type="number" step="0.01" data-testid={`order-item-price-${i}`}
                  className={inputCls + (canOverridePrice ? "" : " bg-[#F4F3ED] cursor-not-allowed")}
                  value={it.unit_price}
                  placeholder={t("orders.unitPrice")}
                  readOnly={!canOverridePrice}
                  title={!canOverridePrice ? t("orders.priceFromMaster") : undefined}
                  onChange={(e) => canOverridePrice && setItem(i, "unit_price", e.target.value)} />
                <input type="number" step="0.01" data-testid={`order-item-discount-${i}`} className={inputCls} value={it.discount} placeholder="Disc %"
                  onChange={(e) => setItem(i, "discount", e.target.value)} />
                <button type="button" onClick={() => removeItem(i)} className="p-2 text-[#8F8D84] hover:text-[#8C3B3B]">
                  <Trash size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <Field label={t("orders.internalNotes")}>
        <textarea rows={2} data-testid="order-notes" className={inputCls} value={form.internal_notes}
          onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} />
      </Field>

      <PrimaryBtn testId="order-submit-btn">
        {t(mto ? "orders.createMto" : "orders.createOrder")}
      </PrimaryBtn>
    </form>
  );
}

function OrdersList({ source, mto }) {
  const { can } = useAuth();
  const { t, tEnum, fmtDateTime } = useI18n();
  const params = new URLSearchParams();
  if (source) params.set("source", source);
  const [rows, reload] = useList(`/orders${params.toString() ? "?" + params.toString() : ""}`, [source]);
  const [customers] = useList("/customers");
  const [openNew, setOpenNew] = useState(false);
  const custMap = Object.fromEntries(customers.map((c) => [c.id, c]));
  const nav = useNavigate();

  const filtered = mto ? rows.filter((r) => r.is_mto) : (source ? rows : rows);

  return (
    <div data-testid={`orders-page${source ? "-" + source : mto ? "-mto" : ""}`}>
      <PageHeader
        eyebrow={t(mto ? "mto.eyebrow" : "orders.eyebrow")}
        title={t(mto ? "mto.title" : source === "showroom" ? "orders.showroomTitle" : "orders.title")}
        subtitle={t(mto ? "mto.subtitle" : "orders.subtitle")}
        actions={can("order:create") && (
          <PrimaryBtn type="button" onClick={() => setOpenNew(true)} testId="new-order-btn">
            <Plus size={14} /> {t(mto ? "mto.new" : "orders.new")}
          </PrimaryBtn>
        )}
      />
      <SectionCard testId="orders-table-card">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("orders.orderNumber")}</th>
              <th className="data-label px-6 py-3 text-left">{t("orders.customer")}</th>
              <th className="data-label px-6 py-3 text-left">{t("orders.source")}</th>
              <th className="data-label px-6 py-3 text-left">{t("orders.priority")}</th>
              <th className="data-label px-6 py-3 text-left">{t("orders.date")}</th>
              <th className="data-label px-6 py-3 text-left">{t("orders.deliveryDate")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <EmptyRow colSpan={7} text={t("orders.empty")} />}
            {filtered.map((o) => (
              <tr key={o.id} className="border-t h-14 cursor-pointer hover:bg-[#F4F3ED]"
                style={{ borderColor: "var(--nylsu-border)" }}
                onClick={() => nav(`/orders/${o.id}`)}
                data-testid={`order-row-${o.order_number}`}>
                <td className="px-6 font-mono text-xs">{o.order_number}</td>
                <td className="px-6 font-serif">{custMap[o.customer_id]?.name || "—"}</td>
                <td className="px-6 text-xs uppercase tracking-[0.1em]">{tEnum("order.source", o.source)}</td>
                <td className="px-6"><StatusPill status={tEnum("priority", o.priority)} tone={o.priority === "urgent" || o.priority === "vip" ? "danger" : "gold"} /></td>
                <td className="px-6 text-xs">{fmtDateTime(o.order_date)}</td>
                <td className="px-6 text-xs">{o.required_delivery_date || o.event_date || "—"}</td>
                <td className="px-6"><StatusPill status={tEnum("order.status", o.status)} tone={orderStatusTone(o.status)} testId={`order-status-${o.order_number}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
      {openNew && (
        <Modal wide onClose={() => setOpenNew(false)} title={t(mto ? "mto.new" : "orders.new")}>
          <NewOrderForm mto={mto} onSaved={(o) => { setOpenNew(false); reload(); nav(`/orders/${o.id}`); }} />
        </Modal>
      )}
    </div>
  );
}

export function AllOrdersPage() { return <OrdersList />; }
export function ShowroomOrdersPage() { return <OrdersList source="showroom" />; }
export function MadeToOrderPage() { return <OrdersList mto />; }

/* ─────────────── Order Detail ─────────────── */
export function OrderDetailPage() {
  const { orderId } = useParams();
  const { can } = useAuth();
  const { t, tEnum, fmtDateTime, fmtCurrency } = useI18n();
  const [order, setOrder] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [customer, setCustomer] = useState(null);
  const nav = useNavigate();

  const reload = async () => {
    try {
      const [o, s] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get(`/orders/${orderId}/suggested-reservations`).catch(() => ({ data: [] })),
      ]);
      setOrder(o.data);
      setSuggested(s.data || []);
      if (o.data.customer_id) {
        const c = await api.get(`/customers/${o.data.customer_id}`).catch(() => null);
        if (c) setCustomer(c.data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [orderId]);

  const confirm = async () => {
    try { await api.post(`/orders/${orderId}/confirm`); toast.success(t("orders.confirmedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reserve = async (order_item_id, quantity) => {
    try { await api.post(`/orders/${orderId}/reserve`, { order_item_id, quantity }); toast.success(t("orders.reservedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const createReq = async (order_item_id, quantity) => {
    try { await api.post(`/orders/${orderId}/create-production-requirement`, { order_item_id, quantity }); toast.success(t("orders.reqCreatedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = async () => {
    if (!window.confirm(t("orders.cancelConfirm"))) return;
    try { await api.post(`/orders/${orderId}/cancel`); toast.success(t("orders.cancelledToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!order) return <div className="text-sm text-[#8F8D84]">{t("common.loading")}</div>;

  const isMto = order.is_mto;

  return (
    <div data-testid="order-detail-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">{tEnum("order.source", order.source)} · {fmtDateTime(order.order_date)}</div>
          <h1 className="font-serif text-4xl tracking-tight font-light">{order.order_number}</h1>
          <div className="mt-2 flex items-center gap-3">
            <StatusPill status={tEnum("order.status", order.status)} tone={orderStatusTone(order.status)} testId="order-status" />
            <StatusPill status={tEnum("priority", order.priority)} tone={order.priority === "urgent" || order.priority === "vip" ? "danger" : "gold"} />
            {customer?.vip && <StatusPill status="VIP" tone="gold" />}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {order.status === "draft" && can("order:edit") && (
            <PrimaryBtn type="button" onClick={confirm} testId="order-confirm-btn">
              <CheckCircle size={14} /> {t("orders.confirm")}
            </PrimaryBtn>
          )}
          {!["cancelled", "delivered"].includes(order.status) && can("order:edit") && (
            <GhostBtn onClick={cancel} testId="order-cancel-btn">
              <X size={14} /> {t("orders.cancel")}
            </GhostBtn>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <SectionCard testId="order-customer-card">
          <div className="p-6">
            <div className="overline mb-2">{t("orders.customer")}</div>
            <div className="font-serif text-2xl">{customer?.name || "—"}</div>
            <div className="text-xs text-[#5C5A54] mt-2">{customer?.email}</div>
            <div className="text-xs text-[#5C5A54]">{customer?.phone}</div>
          </div>
        </SectionCard>
        <SectionCard>
          <div className="p-6">
            <div className="overline mb-2">{t("orders.delivery")}</div>
            <div className="font-serif text-lg">{order.required_delivery_date || t("orders.noDate")}</div>
            {isMto && order.event_date && (
              <div className="mt-2 text-xs">{t("orders.eventDate")}: <span className="font-mono">{order.event_date}</span></div>
            )}
          </div>
        </SectionCard>
        <SectionCard>
          <div className="p-6">
            <div className="overline mb-2">{t("orders.total")}</div>
            <div className="font-serif text-3xl font-light">{fmtCurrency(order.total || 0, order.currency)}</div>
          </div>
        </SectionCard>
      </div>

      {/* MTO — measurements + timeline */}
      {isMto && order.mto_measurements && (
        <SectionCard testId="mto-block">
          <div className="p-6">
            <div className="overline mb-3">{t("mto.measurements")}</div>
            <div className="grid grid-cols-4 gap-4 mb-6">
              {Object.entries(order.mto_measurements).filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                <div key={k}>
                  <div className="data-label">{t("mto.m." + k, k)}</div>
                  <div className="font-mono text-lg mt-1">{v} cm</div>
                </div>
              ))}
            </div>
            {order.mto_special_instructions && (
              <div className="border-t pt-4 text-sm" style={{ borderColor: "var(--nylsu-border)" }}>
                <div className="data-label mb-1">{t("mto.specialInstructions")}</div>
                {order.mto_special_instructions}
              </div>
            )}
          </div>
        </SectionCard>
      )}

      <div className="mt-8">
        <div className="overline mb-3">{t("orders.items")}</div>
        <SectionCard testId="order-items-card">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--nylsu-table-head)" }}>
                <th className="data-label px-6 py-3 text-left">SKU</th>
                <th className="data-label px-6 py-3 text-left">{t("common.color")} / {t("common.size")}</th>
                <th className="data-label px-6 py-3 text-right">{t("orders.qty")}</th>
                <th className="data-label px-6 py-3 text-right">{t("orders.reserved")}</th>
                <th className="data-label px-6 py-3 text-right">{t("orders.unitPrice")}</th>
                <th className="data-label px-6 py-3 text-right">{t("orders.total")}</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((it) => (
                <tr key={it.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}
                  data-testid={`order-item-${it.sku}`}>
                  <td className="px-6 font-mono text-xs">{it.sku}</td>
                  <td className="px-6 text-xs">{it.color} / {it.size}</td>
                  <td className="px-6 text-right font-mono">{it.quantity}</td>
                  <td className="px-6 text-right font-mono" style={{ color: (it.reserved_qty || 0) >= it.quantity ? "var(--nylsu-success)" : "var(--nylsu-warning)" }}>
                    {it.reserved_qty || 0}
                  </td>
                  <td className="px-6 text-right font-mono text-xs">{fmtCurrency(it.final_unit_price, order.currency)}</td>
                  <td className="px-6 text-right font-mono">{fmtCurrency(it.total, order.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      </div>

      {/* Suggested reservations panel */}
      {suggested.length > 0 && !["cancelled", "delivered", "ready", "packed"].includes(order.status) && (
        <div className="mt-8" data-testid="suggested-reservations-panel">
          <div className="overline mb-3">{t("orders.suggested")}</div>
          <SectionCard>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--nylsu-table-head)" }}>
                  <th className="data-label px-6 py-3 text-left">SKU</th>
                  <th className="data-label px-6 py-3 text-right">{t("orders.requested")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("orders.availableStock")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("orders.canReserve")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("orders.needsProduction")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {suggested.map((s) => (
                  <tr key={s.order_item_id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}
                    data-testid={`suggested-row-${s.sku}`}>
                    <td className="px-6 font-mono text-xs">{s.sku}</td>
                    <td className="px-6 text-right font-mono">{s.requested}</td>
                    <td className="px-6 text-right font-mono">{s.available}</td>
                    <td className="px-6 text-right font-mono" style={{ color: "var(--nylsu-success)" }}>{s.can_reserve}</td>
                    <td className="px-6 text-right font-mono" style={{ color: s.needs_production > 0 ? "var(--nylsu-warning)" : undefined }}>{s.needs_production}</td>
                    <td className="px-6 text-right">
                      <div className="flex gap-2 justify-end">
                        {s.can_reserve > 0 && can("reservation:create") && (
                          <button data-testid={`reserve-btn-${s.sku}`} onClick={() => reserve(s.order_item_id, s.can_reserve)}
                            className="text-xs uppercase tracking-[0.15em] px-3 py-2 border hover:bg-[#1A1A1A] hover:text-white"
                            style={{ borderColor: "var(--nylsu-border-strong)" }}>
                            {t("orders.reserveN", { n: s.can_reserve })}
                          </button>
                        )}
                        {s.needs_production > 0 && can("production_requirement:create") && (
                          <button data-testid={`create-req-btn-${s.sku}`} onClick={() => createReq(s.order_item_id, s.needs_production)}
                            className="text-xs uppercase tracking-[0.15em] px-3 py-2 border hover:bg-[#8C3B3B] hover:text-white"
                            style={{ borderColor: "var(--nylsu-warning)", color: "var(--nylsu-warning)" }}>
                            {t("orders.createReqN", { n: s.needs_production })}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   RESERVATIONS
═══════════════════════════════════════════════════════════════════ */
export function ReservationsPage() {
  const { t, tEnum, fmtDateTime } = useI18n();
  const [rows, reload] = useList("/reservations");
  const [orders] = useList("/orders");
  const orderMap = Object.fromEntries(orders.map((o) => [o.id, o]));
  const release = async (id) => {
    try { await api.post(`/reservations/${id}/release`); toast.success(t("common.deletedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="reservations-page">
      <PageHeader eyebrow={t("reservations.eyebrow")} title={t("reservations.title")} subtitle={t("reservations.subtitle")} />
      <SectionCard>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("orders.orderNumber")}</th>
              <th className="data-label px-6 py-3 text-left">SKU</th>
              <th className="data-label px-6 py-3 text-right">{t("orders.qty")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.status")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.timestamp")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={6} text={t("reservations.empty")} />}
            {rows.map((r) => (
              <tr key={r.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}
                data-testid={`reservation-row-${r.id}`}>
                <td className="px-6 font-mono text-xs">
                  <Link className="link-underline" to={`/orders/${r.order_id}`}>
                    {orderMap[r.order_id]?.order_number || r.order_id}
                  </Link>
                </td>
                <td className="px-6 font-mono text-xs">{r.sku}</td>
                <td className="px-6 text-right font-mono">{r.quantity}</td>
                <td className="px-6"><StatusPill status={tEnum("reservation.status", r.status)} tone={r.status === "active" ? "success" : "muted"} /></td>
                <td className="px-6 text-xs">{fmtDateTime(r.created_at)}</td>
                <td className="px-6 text-right">
                  {r.status === "active" && (
                    <button onClick={() => release(r.id)} data-testid={`release-${r.id}`}
                      className="text-xs uppercase tracking-[0.15em] text-[#8C3B3B]">{t("reservations.release")}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PRODUCTION PLANNING
═══════════════════════════════════════════════════════════════════ */
export function ProductionPlanningPage() {
  const { can } = useAuth();
  const { t, tEnum } = useI18n();
  const [rows, reload] = useList("/production-planning");
  const [styles] = useList("/styles");
  const [locations] = useList("/locations");
  const [openPO, setOpenPO] = useState(null);
  const styleMap = Object.fromEntries(styles.map((s) => [s.id, s]));

  return (
    <div data-testid="production-planning-page">
      <PageHeader eyebrow={t("planning.eyebrow")} title={t("planning.title")} subtitle={t("planning.subtitle")} />
      <SectionCard>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">SKU</th>
              <th className="data-label px-6 py-3 text-left">{t("planning.style")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.color")}</th>
              <th className="data-label px-6 py-3 text-right">{t("planning.demand")}</th>
              <th className="data-label px-6 py-3 text-right">{t("planning.availableFG")}</th>
              <th className="data-label px-6 py-3 text-right">{t("planning.inProduction")}</th>
              <th className="data-label px-6 py-3 text-right">{t("planning.net")}</th>
              <th className="data-label px-6 py-3 text-left">{t("orders.priority")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={9} text={t("planning.empty")} />}
            {rows.map((r) => (
              <tr key={r.sku} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}
                data-testid={`planning-row-${r.sku}`}>
                <td className="px-6 font-mono text-xs">{r.sku}</td>
                <td className="px-6">{styleMap[r.style_id]?.name || r.style_id}</td>
                <td className="px-6 text-xs">{r.color}</td>
                <td className="px-6 text-right font-mono">{r.demand}</td>
                <td className="px-6 text-right font-mono">{r.available_fg}</td>
                <td className="px-6 text-right font-mono">{r.in_production}</td>
                <td className="px-6 text-right font-mono" style={{ color: r.net_requirement > 0 ? "var(--nylsu-warning)" : "var(--nylsu-success)" }}>
                  {r.net_requirement}
                </td>
                <td className="px-6"><StatusPill status={tEnum("priority", r.priority)} tone={r.priority === "urgent" || r.priority === "vip" ? "danger" : "gold"} /></td>
                <td className="px-6 text-right">
                  {r.net_requirement > 0 && can("production_order:create") && (
                    <button data-testid={`create-po-${r.sku}`} onClick={() => setOpenPO(r)}
                      className="text-xs uppercase tracking-[0.15em] link-underline">
                      {t("planning.createPO")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
      {openPO && (
        <Modal wide onClose={() => setOpenPO(null)} title={t("po.new")}>
          <NewProductionOrderForm
            planning={openPO}
            styles={styles}
            locations={locations}
            onSaved={() => { setOpenPO(null); reload(); }}
          />
        </Modal>
      )}
    </div>
  );
}

function NewProductionOrderForm({ planning, styles, locations, onSaved }) {
  const { t, tEnum } = useI18n();
  const style = styles.find((s) => s.id === planning?.style_id) || styles[0];
  const [form, setForm] = useState(() => ({
    style_id: style?.id || "",
    color: planning?.color || (style?.variants?.[0]?.color || ""),
    size_breakdown: planning ? { [planning.size]: planning.net_requirement } : {},
    location_id: locations[0]?.id || "",
    priority: planning?.priority || "normal",
    planned_completion_date: "",
    notes: "",
    linked_requirement_ids: planning?.requirements?.map((r) => r.id) || [],
    linked_order_ids: [...new Set((planning?.requirements || []).filter((r) => r.source_type === "customer_order").map((r) => r.source_id))],
  }));
  const currentStyle = styles.find((s) => s.id === form.style_id);
  const colors = [...new Set((currentStyle?.variants || []).map((v) => v.color))];
  const sizes = [...new Set((currentStyle?.variants || []).map((v) => v.size))];

  const total = Object.values(form.size_breakdown).reduce((a, b) => a + Number(b || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const cleaned = Object.fromEntries(
        Object.entries(form.size_breakdown).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])
      );
      if (Object.keys(cleaned).length === 0) { toast.error(t("po.needSizeBreakdown")); return; }
      const r = await api.post("/production-orders", { ...form, size_breakdown: cleaned });
      toast.success(t("po.createdToast", { number: r.data.po_number }));
      onSaved(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="new-po-form">
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("planning.style")}>
          <select data-testid="po-style-select" className={inputCls} value={form.style_id}
            onChange={(e) => setForm({ ...form, style_id: e.target.value })}>
            {styles.map((s) => <option key={s.id} value={s.id}>{s.style_code} — {s.name}</option>)}
          </select>
        </Field>
        <Field label={t("common.color")}>
          <select data-testid="po-color-select" className={inputCls} value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}>
            {colors.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <div>
        <div className="data-label mb-3">{t("po.sizeBreakdown")}</div>
        <div className="grid grid-cols-6 gap-3">
          {sizes.map((sz) => (
            <label key={sz} className="block">
              <div className="text-xs uppercase tracking-[0.15em] text-[#8F8D84] mb-1">{sz}</div>
              <input type="number" min="0" data-testid={`po-size-${sz}`} className={inputCls}
                value={form.size_breakdown[sz] ?? ""}
                onChange={(e) => setForm({ ...form, size_breakdown: { ...form.size_breakdown, [sz]: e.target.value } })}
                placeholder="0" />
            </label>
          ))}
        </div>
        <div className="mt-2 text-xs text-[#5C5A54]">{t("po.total")}: <span className="font-mono">{total}</span></div>
      </div>
      <div className="grid grid-cols-3 gap-6">
        <Field label={t("common.location")}>
          <select className={inputCls} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label={t("orders.priority")}>
          <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{tEnum("priority", p)}</option>)}
          </select>
        </Field>
        <Field label={t("po.plannedCompletion")}>
          <input type="date" className={inputCls} value={form.planned_completion_date}
            onChange={(e) => setForm({ ...form, planned_completion_date: e.target.value })} />
        </Field>
      </div>
      <Field label={t("common.notes")}>
        <textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <PrimaryBtn testId="po-submit-btn">{t("po.createCta")}</PrimaryBtn>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PRODUCTION ORDERS LIST + DETAIL
═══════════════════════════════════════════════════════════════════ */
export function ProductionOrdersPage() {
  const { can } = useAuth();
  const { t, tEnum, fmtDateTime } = useI18n();
  const [rows, reload] = useList("/production-orders");
  const [styles] = useList("/styles");
  const [locations] = useList("/locations");
  const [openNew, setOpenNew] = useState(false);
  const nav = useNavigate();

  return (
    <div data-testid="production-orders-page">
      <PageHeader eyebrow={t("po.eyebrow")} title={t("po.title")} subtitle={t("po.subtitle")}
        actions={can("production_order:create") && (
          <PrimaryBtn type="button" onClick={() => setOpenNew(true)} testId="new-po-btn">
            <Plus size={14} /> {t("po.new")}
          </PrimaryBtn>
        )} />
      <SectionCard>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("po.number")}</th>
              <th className="data-label px-6 py-3 text-left">{t("planning.style")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.color")}</th>
              <th className="data-label px-6 py-3 text-right">{t("po.qty")}</th>
              <th className="data-label px-6 py-3 text-left">{t("po.stage")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.status")}</th>
              <th className="data-label px-6 py-3 text-left">{t("orders.priority")}</th>
              <th className="data-label px-6 py-3 text-left">{t("po.plannedCompletion")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={8} text={t("po.empty")} />}
            {rows.map((p) => (
              <tr key={p.id} className="border-t h-14 cursor-pointer hover:bg-[#F4F3ED]"
                style={{ borderColor: "var(--nylsu-border)" }}
                onClick={() => nav(`/production/orders/${p.id}`)}
                data-testid={`po-row-${p.po_number}`}>
                <td className="px-6 font-mono text-xs">{p.po_number}</td>
                <td className="px-6 font-serif">{p.style_name}</td>
                <td className="px-6 text-xs">{p.color}</td>
                <td className="px-6 text-right font-mono">{p.total_quantity}</td>
                <td className="px-6 text-xs">{p.current_stage}</td>
                <td className="px-6"><StatusPill status={tEnum("po.status", p.status)} tone={poStatusTone(p.status)} testId={`po-status-${p.po_number}`} /></td>
                <td className="px-6"><StatusPill status={tEnum("priority", p.priority)} tone={p.priority === "urgent" || p.priority === "vip" ? "danger" : "gold"} /></td>
                <td className="px-6 text-xs">{p.planned_completion_date || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
      {openNew && (
        <Modal wide onClose={() => setOpenNew(false)} title={t("po.new")}>
          <NewProductionOrderForm styles={styles} locations={locations}
            onSaved={(p) => { setOpenNew(false); reload(); nav(`/production/orders/${p.id}`); }} />
        </Modal>
      )}
    </div>
  );
}

/* ─────────────── PO Detail ─────────────── */
export function ProductionOrderDetailPage() {
  const { poId } = useParams();
  const { can } = useAuth();
  const { t, tEnum, fmtDateTime } = useI18n();
  const [po, setPo] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [reserveMat, setReserveMat] = useState(null);
  const [consumeMat, setConsumeMat] = useState(null);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get(`/production-orders/${poId}`);
      setPo(r.data);
      if (r.data.style_id) {
        const list = await fetchPrimaryAttachments("style", [r.data.style_id]);
        const map = bulkPrimaryMap(list);
        setThumb(map[r.data.style_id]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [poId]);

  const cancel = async () => {
    if (!window.confirm(t("po.cancelConfirm"))) return;
    try { await api.post(`/production-orders/${poId}/cancel`); toast.success(t("po.cancelledToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!po) return <div className="text-sm text-[#8F8D84]">{t("common.loading")}</div>;
  const materials = po.materials_status || po.materials || [];

  return (
    <div data-testid="po-detail-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div className="flex items-start gap-6">
          {thumb && <Thumb attachment={thumb} size={96} alt={po.style_name} />}
          <div>
            <div className="overline mb-2">{po.style_code}</div>
            <h1 className="font-serif text-4xl tracking-tight font-light">{po.po_number}</h1>
            <div className="font-serif text-xl text-[#5C5A54] mt-1">{po.style_name} · {po.color}</div>
            <div className="mt-3 flex items-center gap-3">
              <StatusPill status={tEnum("po.status", po.status)} tone={poStatusTone(po.status)} testId="po-status" />
              <StatusPill status={po.current_stage} tone="gold" />
              <StatusPill status={tEnum("priority", po.priority)} tone={po.priority === "urgent" || po.priority === "vip" ? "danger" : "gold"} />
              <span className="font-mono text-sm text-[#5C5A54]">{po.total_quantity} {t("po.units")}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!["completed", "cancelled"].includes(po.status) && can("production_stage:update") && (
            <PrimaryBtn type="button" onClick={() => setAdvanceOpen(true)} testId="po-advance-btn">
              <ArrowRight size={14} /> {t("po.advanceStage")}
            </PrimaryBtn>
          )}
          {!["completed", "cancelled"].includes(po.status) && can("qc:create") && (
            <GhostBtn onClick={() => setQcOpen(true)} testId="po-qc-btn">
              <ClipboardText size={14} /> {t("qc.record")}
            </GhostBtn>
          )}
          {po.status !== "cancelled" && can("finished_goods:create") && (
            <GhostBtn onClick={() => setFinishOpen(true)} testId="po-finish-btn">
              <Check size={14} /> {t("po.receiveFinished")}
            </GhostBtn>
          )}
          {!["completed", "cancelled"].includes(po.status) && can("production_order:edit") && (
            <GhostBtn onClick={cancel} testId="po-cancel-btn"><X size={14} /> {t("po.cancel")}</GhostBtn>
          )}
        </div>
      </div>

      {/* Size breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <SectionCard testId="po-sizes-card">
          <div className="p-6">
            <div className="overline mb-3">{t("po.sizeBreakdown")}</div>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(po.size_breakdown || {}).map(([sz, q]) => (
                <div key={sz} className="border p-3" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`po-size-${sz}`}>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#8F8D84]">{sz}</div>
                  <div className="font-mono text-2xl mt-1">{q}</div>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
        <SectionCard>
          <div className="p-6">
            <div className="overline mb-3">{t("po.materialAvailability")}</div>
            <div className="text-3xl font-serif" style={{ color: po.material_availability === "red" ? "var(--nylsu-danger)" : po.material_availability === "amber" ? "var(--nylsu-warning)" : "var(--nylsu-success)" }}>
              {t("po.material." + (po.material_availability || "green"))}
            </div>
          </div>
        </SectionCard>
        <SectionCard>
          <div className="p-6">
            <div className="overline mb-3">{t("po.completion")}</div>
            <div className="font-serif text-lg">{po.planned_completion_date || t("orders.noDate")}</div>
          </div>
        </SectionCard>
      </div>

      {/* Materials */}
      <div className="mb-8">
        <div className="overline mb-3">{t("po.materials")}</div>
        <SectionCard>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--nylsu-table-head)" }}>
                <th className="data-label px-4 py-3 text-left" style={{ width: 56 }}>{t("common.image")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.code")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
                <th className="data-label px-6 py-3 text-right">{t("po.stdQty")}</th>
                <th className="data-label px-6 py-3 text-right">{t("po.requiredQty")}</th>
                <th className="data-label px-6 py-3 text-right">{t("po.reservedQty")}</th>
                <th className="data-label px-6 py-3 text-right">{t("po.consumedQty")}</th>
                <th className="data-label px-6 py-3 text-right">{t("po.availableQty")}</th>
                <th className="data-label px-6 py-3 text-right">{t("po.shortage")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {materials.length === 0 && <EmptyRow colSpan={10} text={t("po.noMaterials")} />}
              {materials.map((m) => {
                const remaining = Number(m.required_quantity || 0) - Number(m.reserved_quantity || 0);
                return (
                  <tr key={m.material_id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}
                    data-testid={`po-material-${m.material_code}`}>
                    <td className="px-4 py-2"><POMaterialThumb materialId={m.material_id} name={m.material_name} /></td>
                    <td className="px-6 font-mono text-xs">{m.material_code}</td>
                    <td className="px-6 text-xs">{m.material_name}</td>
                    <td className="px-6 text-right font-mono">{Number(m.standard_quantity || 0).toFixed(2)}</td>
                    <td className="px-6 text-right font-mono">{Number(m.required_quantity || 0).toFixed(2)}</td>
                    <td className="px-6 text-right font-mono">{Number(m.reserved_quantity || 0).toFixed(2)}</td>
                    <td className="px-6 text-right font-mono">{Number(m.consumed_quantity || 0).toFixed(2)}</td>
                    <td className="px-6 text-right font-mono">{m.available != null ? Number(m.available).toFixed(2) : "—"}</td>
                    <td className="px-6 text-right font-mono" style={{ color: m.shortage > 0 ? "var(--nylsu-danger)" : "var(--nylsu-success)" }}>
                      {Number(m.shortage || 0).toFixed(2)}
                    </td>
                    <td className="px-6 text-right">
                      <div className="flex gap-2 justify-end">
                        {remaining > 0.001 && can("material_reservation:create") && (
                          <button data-testid={`reserve-mat-${m.material_code}`} onClick={() => setReserveMat(m)}
                            className="text-xs uppercase tracking-[0.15em] link-underline">{t("po.reserve")}</button>
                        )}
                        {Number(m.reserved_quantity || 0) > 0 && can("material_reservation:consume") && (
                          <button data-testid={`consume-mat-${m.material_code}`} onClick={() => setConsumeMat(m)}
                            className="text-xs uppercase tracking-[0.15em] link-underline">{t("po.consume")}</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SectionCard>
      </div>

      {/* Items with QC / Received */}
      <div className="mb-8">
        <div className="overline mb-3">{t("po.items")}</div>
        <SectionCard>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--nylsu-table-head)" }}>
                <th className="data-label px-6 py-3 text-left">SKU</th>
                <th className="data-label px-6 py-3 text-left">{t("common.size")}</th>
                <th className="data-label px-6 py-3 text-right">{t("po.qty")}</th>
                <th className="data-label px-6 py-3 text-right">{t("qc.passed")}</th>
                <th className="data-label px-6 py-3 text-right">{t("qc.failed")}</th>
                <th className="data-label px-6 py-3 text-right">{t("po.received")}</th>
              </tr>
            </thead>
            <tbody>
              {(po.items || []).map((i) => (
                <tr key={i.id} className="border-t h-12" style={{ borderColor: "var(--nylsu-border)" }}
                  data-testid={`po-item-${i.sku}`}>
                  <td className="px-6 font-mono text-xs">{i.sku}</td>
                  <td className="px-6 text-xs">{i.size}</td>
                  <td className="px-6 text-right font-mono">{i.quantity}</td>
                  <td className="px-6 text-right font-mono" style={{ color: "var(--nylsu-success)" }}>{i.qc_passed || 0}</td>
                  <td className="px-6 text-right font-mono" style={{ color: "var(--nylsu-danger)" }}>{i.qc_failed || 0}</td>
                  <td className="px-6 text-right font-mono">{i.received || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      </div>

      {/* Stage history */}
      {po.stage_history?.length > 0 && (
        <div className="mb-8">
          <div className="overline mb-3">{t("po.stageHistory")}</div>
          <SectionCard>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--nylsu-table-head)" }}>
                  <th className="data-label px-6 py-3 text-left">{t("common.timestamp")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("po.from")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("po.to")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("po.qty")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.user")}</th>
                </tr>
              </thead>
              <tbody>
                {po.stage_history.map((h) => (
                  <tr key={h.id} className="border-t" style={{ borderColor: "var(--nylsu-border)" }}>
                    <td className="px-6 font-mono text-xs">{fmtDateTime(h.timestamp)}</td>
                    <td className="px-6 text-xs">{h.from_stage}</td>
                    <td className="px-6 text-xs">{h.to_stage}</td>
                    <td className="px-6 text-right font-mono">{h.quantity}</td>
                    <td className="px-6 text-xs">{h.user_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        </div>
      )}

      {/* Modals */}
      {reserveMat && (
        <Modal onClose={() => setReserveMat(null)} title={t("po.reserveMaterial") + " · " + reserveMat.material_code}>
          <ReserveMaterialForm po={po} material={reserveMat} onSaved={() => { setReserveMat(null); reload(); }} />
        </Modal>
      )}
      {consumeMat && (
        <Modal onClose={() => setConsumeMat(null)} title={t("po.consumeMaterial") + " · " + consumeMat.material_code}>
          <ConsumeMaterialForm po={po} material={consumeMat} onSaved={() => { setConsumeMat(null); reload(); }} />
        </Modal>
      )}
      {advanceOpen && (
        <Modal onClose={() => setAdvanceOpen(false)} title={t("po.advanceStage")}>
          <AdvanceStageForm po={po} onSaved={() => { setAdvanceOpen(false); reload(); }} />
        </Modal>
      )}
      {qcOpen && (
        <Modal onClose={() => setQcOpen(false)} title={t("qc.record")}>
          <QCForm po={po} onSaved={() => { setQcOpen(false); reload(); }} />
        </Modal>
      )}
      {finishOpen && (
        <Modal onClose={() => setFinishOpen(false)} title={t("po.receiveFinished")}>
          <FinishForm po={po} onSaved={() => { setFinishOpen(false); reload(); }} />
        </Modal>
      )}
    </div>
  );
}

/* ─────────────── PO Detail Forms ─────────────── */
function ReserveMaterialForm({ po, material, onSaved }) {
  const { t } = useI18n();
  const isFabric = /^FAB-/i.test(material.material_code) || /(meter|m)/i.test(material.unit || "");
  const remaining = Number(material.required_quantity) - Number(material.reserved_quantity || 0);
  const [rolls, setRolls] = useState([]);
  const [qty, setQty] = useState(remaining);
  const [alloc, setAlloc] = useState({}); // { roll_id: meters }
  useEffect(() => {
    if (isFabric) {
      api.get(`/fabric-rolls/available?material_id=${material.material_id}`).then((r) => setRolls(r.data));
    }
  }, [isFabric, material.material_id]);
  const allocSum = Object.values(alloc).reduce((a, b) => a + Number(b || 0), 0);
  const submit = async (e) => {
    e.preventDefault();
    const payload = { material_id: material.material_id, quantity: Number(qty) };
    if (isFabric && rolls.length > 0) {
      payload.roll_allocations = Object.entries(alloc).filter(([, m]) => Number(m) > 0).map(([roll_id, meters]) => ({ roll_id, meters: Number(meters) }));
    }
    try { await api.post(`/production-orders/${po.id}/reserve-material`, payload); toast.success(t("po.reservedToast")); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="reserve-mat-form">
      <div className="text-sm">
        <span className="text-[#8F8D84]">{t("po.requiredQty")}:</span> <span className="font-mono">{material.required_quantity}</span> · {" "}
        <span className="text-[#8F8D84]">{t("po.reservedQty")}:</span> <span className="font-mono">{material.reserved_quantity || 0}</span> · {" "}
        <span className="text-[#8F8D84]">{t("po.availableQty")}:</span> <span className="font-mono">{material.available}</span>
      </div>
      <Field label={t("po.reserveQty")}>
        <input required type="number" step="0.01" data-testid="reserve-qty-input" className={inputCls}
          value={qty} onChange={(e) => setQty(e.target.value)} max={remaining} />
      </Field>
      {isFabric && rolls.length > 0 && (
        <div>
          <div className="data-label mb-3">{t("po.rollAllocation")}</div>
          <div className="space-y-2">
            {rolls.map((r) => (
              <div key={r.id} className="grid grid-cols-[2fr_1fr_120px] gap-3 items-center" data-testid={`roll-alloc-${r.roll_number}`}>
                <div className="text-xs">{r.roll_number} <span className="text-[#8F8D84]">· {r.dye_lot}</span></div>
                <div className="text-xs font-mono">{r.available_meters} m</div>
                <input type="number" step="0.1" min="0" max={r.available_meters}
                  data-testid={`roll-alloc-input-${r.roll_number}`}
                  className={inputCls} value={alloc[r.id] || ""} placeholder="0"
                  onChange={(e) => setAlloc({ ...alloc, [r.id]: e.target.value })} />
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs">
            {t("po.allocated")}: <span className="font-mono">{allocSum.toFixed(2)}</span> / {Number(qty).toFixed(2)}
          </div>
        </div>
      )}
      <PrimaryBtn testId="reserve-mat-submit">{t("po.reserve")}</PrimaryBtn>
    </form>
  );
}

const CONSUMPTION_REASONS = ["Cutting Waste", "Defect", "Recut", "Sample", "Measurement Difference", "Other"];

function ConsumeMaterialForm({ po, material, onSaved }) {
  const { t } = useI18n();
  const isFabric = /^FAB-/i.test(material.material_code) || /(meter|m)/i.test(material.unit || "");
  const [rolls, setRolls] = useState([]);
  const [qty, setQty] = useState(material.standard_quantity || material.required_quantity);
  const [rollId, setRollId] = useState("");
  const [reason, setReason] = useState("");
  const std = Number(material.standard_quantity || 0);
  const variance = Number(qty) - std;
  const variancePct = std > 0 ? (variance / std * 100).toFixed(2) : "0.00";
  useEffect(() => {
    if (isFabric) api.get(`/fabric-rolls/available?material_id=${material.material_id}`).then((r) => setRolls(r.data));
  }, [isFabric, material.material_id]);
  const submit = async (e) => {
    e.preventDefault();
    const payload = { material_id: material.material_id, actual_quantity: Number(qty), variance_reason: reason || null };
    if (rollId) payload.roll_id = rollId;
    try { await api.post(`/production-orders/${po.id}/consume-material`, payload); toast.success(t("po.consumedToast")); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="consume-mat-form">
      <div className="text-sm">
        <span className="text-[#8F8D84]">{t("po.stdQty")}:</span> <span className="font-mono">{std}</span> · {" "}
        <span className="text-[#8F8D84]">{t("po.reservedQty")}:</span> <span className="font-mono">{material.reserved_quantity || 0}</span>
      </div>
      <Field label={t("po.actualQty")}>
        <input required type="number" step="0.01" data-testid="consume-qty-input" className={inputCls}
          value={qty} onChange={(e) => setQty(e.target.value)} />
      </Field>
      <div className="text-xs text-[#5C5A54]">
        {t("po.variance")}: <span className="font-mono" style={{ color: Math.abs(variance) > 0.5 ? "var(--nylsu-warning)" : undefined }}>
          {variance >= 0 ? "+" : ""}{variance.toFixed(2)} ({variancePct}%)
        </span>
      </div>
      {isFabric && rolls.length > 0 && (
        <Field label={t("po.consumeFromRoll")}>
          <select data-testid="consume-roll-select" className={inputCls} value={rollId} onChange={(e) => setRollId(e.target.value)}>
            <option value="">—</option>
            {rolls.map((r) => <option key={r.id} value={r.id}>{r.roll_number} — {r.current_meters} m</option>)}
          </select>
        </Field>
      )}
      {Math.abs(variance) > 0.01 && (
        <Field label={t("po.varianceReason")}>
          <select className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">—</option>
            {CONSUMPTION_REASONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </Field>
      )}
      <PrimaryBtn testId="consume-mat-submit">{t("po.consume")}</PrimaryBtn>
    </form>
  );
}

function AdvanceStageForm({ po, onSaved }) {
  const { t } = useI18n();
  const stages = po.stages || [];
  const currentIdx = stages.indexOf(po.current_stage);
  const nextIdx = currentIdx >= 0 && currentIdx < stages.length - 1 ? currentIdx + 1 : currentIdx;
  const [to, setTo] = useState(stages[nextIdx] || stages[0] || "");
  const [qty, setQty] = useState(po.total_quantity);
  const [notes, setNotes] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/production-orders/${po.id}/advance-stage`, { to_stage: to, quantity: Number(qty), notes }); toast.success(t("po.stageAdvancedToast")); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="advance-stage-form">
      <div className="text-xs text-[#5C5A54]">{t("po.currentStage")}: <span className="font-mono">{po.current_stage}</span></div>
      <Field label={t("po.toStage")}>
        <select data-testid="advance-to-stage" required className={inputCls} value={to} onChange={(e) => setTo(e.target.value)}>
          {stages.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field label={t("po.qty")}>
        <input required type="number" min="1" data-testid="advance-qty" className={inputCls}
          value={qty} onChange={(e) => setQty(e.target.value)} max={po.total_quantity} />
      </Field>
      <Field label={t("common.notes")}>
        <textarea rows={2} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <PrimaryBtn testId="advance-submit-btn">{t("po.advance")}</PrimaryBtn>
    </form>
  );
}

const QC_REASONS = ["Sewing Defect", "Fabric Defect", "Measurement", "Embroidery", "Finishing", "Stain", "Damage", "Other"];
const QC_DISPOS = ["Rework", "Alteration", "Reject", "Damage / Write-Off"];

function QCForm({ po, onSaved }) {
  const { t } = useI18n();
  const [sku, setSku] = useState(po.items?.[0]?.sku || "");
  const [inspected, setInspected] = useState(0);
  const [passed, setPassed] = useState(0);
  const [failed, setFailed] = useState(0);
  const [reason, setReason] = useState("");
  const [dispo, setDispo] = useState("");
  const [notes, setNotes] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/production-orders/${po.id}/qc`, {
        sku, quantity_inspected: Number(inspected), quantity_passed: Number(passed),
        quantity_failed: Number(failed), failure_reason: reason || null, disposition: dispo || null, notes,
      });
      toast.success(t("qc.recordedToast")); onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="qc-form">
      <Field label={t("qc.sku")}>
        <select required data-testid="qc-sku-select" className={inputCls} value={sku} onChange={(e) => setSku(e.target.value)}>
          {(po.items || []).map((i) => <option key={i.sku} value={i.sku}>{i.sku} · {i.size}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-3 gap-6">
        <Field label={t("qc.inspected")}>
          <input required type="number" min="1" data-testid="qc-inspected" className={inputCls}
            value={inspected} onChange={(e) => { setInspected(e.target.value); setPassed(e.target.value); }} />
        </Field>
        <Field label={t("qc.passed")}>
          <input required type="number" min="0" data-testid="qc-passed" className={inputCls}
            value={passed} onChange={(e) => setPassed(e.target.value)} />
        </Field>
        <Field label={t("qc.failed")}>
          <input required type="number" min="0" data-testid="qc-failed" className={inputCls}
            value={failed} onChange={(e) => setFailed(e.target.value)} />
        </Field>
      </div>
      {Number(failed) > 0 && (
        <>
          <Field label={t("qc.failureReason")}>
            <select required data-testid="qc-reason" className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">—</option>
              {QC_REASONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label={t("qc.disposition")}>
            <select required data-testid="qc-disposition" className={inputCls} value={dispo} onChange={(e) => setDispo(e.target.value)}>
              <option value="">—</option>
              {QC_DISPOS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
        </>
      )}
      <Field label={t("common.notes")}>
        <textarea rows={2} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <PrimaryBtn testId="qc-submit-btn">{t("qc.record")}</PrimaryBtn>
    </form>
  );
}

function FinishForm({ po, onSaved }) {
  const { t } = useI18n();
  const [locations] = useList("/locations");
  const item0 = (po.items || []).find((i) => (i.qc_passed || 0) - (i.received || 0) > 0) || po.items?.[0];
  const [sku, setSku] = useState(item0?.sku || "");
  const [qty, setQty] = useState(0);
  const [locationId, setLocationId] = useState(po.location_id);
  useEffect(() => {
    const it = (po.items || []).find((i) => i.sku === sku);
    if (it) setQty(Math.max(0, (it.qc_passed || 0) - (it.received || 0)));
  }, [sku, po.items]);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/production-orders/${po.id}/finish`, { sku, quantity: Number(qty), location_id: locationId }); toast.success(t("po.receivedToast")); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="finish-form">
      <Field label={t("po.receiveSku")}>
        <select required data-testid="finish-sku-select" className={inputCls} value={sku} onChange={(e) => setSku(e.target.value)}>
          {(po.items || []).map((i) => {
            const avail = Math.max(0, (i.qc_passed || 0) - (i.received || 0));
            return <option key={i.sku} value={i.sku} disabled={avail === 0}>{i.sku} · {i.size} · {avail} {t("po.availableToReceive")}</option>;
          })}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("po.qty")}>
          <input required type="number" min="1" data-testid="finish-qty" className={inputCls} value={qty} onChange={(e) => setQty(e.target.value)} />
        </Field>
        <Field label={t("common.location")}>
          <select className={inputCls} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
      </div>
      <PrimaryBtn testId="finish-submit-btn">{t("po.receive")}</PrimaryBtn>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   WIP BOARD (Kanban)
═══════════════════════════════════════════════════════════════════ */
const WIP_COLUMNS = [
  "Material Preparation", "Cutting", "Sewing", "Handwork",
  "Finishing", "Quality Control", "Packaging", "Finished",
];

export function WIPBoardPage() {
  const { can } = useAuth();
  const { t, tEnum } = useI18n();
  const [pos, reload] = useList("/production-orders");
  const [selected, setSelected] = useState(null);
  const [primaryMap, setPrimaryMap] = useState({});
  const active = pos.filter((p) => !["cancelled"].includes(p.status));
  useEffect(() => {
    const styleIds = [...new Set(active.map((p) => p.style_id))];
    if (styleIds.length) {
      fetchPrimaryAttachments("style", styleIds).then((list) => setPrimaryMap(bulkPrimaryMap(list)));
    }
    // eslint-disable-next-line
  }, [pos.length]);

  return (
    <div data-testid="wip-board-page">
      <PageHeader eyebrow={t("wip.eyebrow")} title={t("wip.title")} subtitle={t("wip.subtitle")} />
      <div className="grid grid-flow-col auto-cols-[280px] gap-4 overflow-x-auto pb-4" data-testid="wip-columns">
        {WIP_COLUMNS.map((col) => {
          const items = active.filter((p) => p.current_stage === col);
          return (
            <div key={col} className="border p-3" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
              data-testid={`wip-col-${col.toLowerCase().replace(/ /g, "-")}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="overline">{col}</div>
                <span className="text-xs font-mono text-[#8F8D84]">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((p) => (
                  <button key={p.id} type="button" onClick={() => setSelected(p)} data-testid={`wip-card-${p.po_number}`}
                    className="w-full text-left border p-3 hover:border-[#C2A878] transition-colors"
                    style={{ borderColor: "var(--nylsu-border)" }}>
                    <div className="flex items-start gap-3">
                      {primaryMap[p.style_id] && <Thumb attachment={primaryMap[p.style_id]} size={48} alt={p.style_name} />}
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[10px] text-[#8F8D84]">{p.po_number}</div>
                        <div className="font-serif text-sm truncate">{p.style_name}</div>
                        <div className="text-xs text-[#5C5A54]">{p.color} · {p.total_quantity} {t("po.units")}</div>
                        <div className="mt-2 flex items-center gap-2">
                          <StatusPill status={tEnum("priority", p.priority)}
                            tone={p.priority === "urgent" || p.priority === "vip" ? "danger" : "gold"} />
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {selected && (
        <Modal wide onClose={() => setSelected(null)} title={selected.po_number}>
          <WIPCardDetail po={selected} onAdvanced={() => { setSelected(null); reload(); }} />
        </Modal>
      )}
    </div>
  );
}

function WIPCardDetail({ po, onAdvanced }) {
  const { can } = useAuth();
  const { t } = useI18n();
  const [full, setFull] = useState(null);
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    api.get(`/production-orders/${po.id}`).then((r) => setFull(r.data));
    fetchPrimaryAttachments("style", [po.style_id]).then((list) => setThumb(bulkPrimaryMap(list)[po.style_id]));
  }, [po.id, po.style_id]);
  if (!full) return <div className="text-sm text-[#8F8D84]">{t("common.loading")}</div>;
  return (
    <div className="space-y-6" data-testid="wip-card-detail">
      <div className="flex gap-6">
        {thumb && <Thumb attachment={thumb} size={140} alt={full.style_name} />}
        <div>
          <div className="overline">{full.style_code}</div>
          <div className="font-serif text-2xl">{full.style_name}</div>
          <div className="text-sm text-[#5C5A54]">{full.color} · {full.total_quantity} {t("po.units")}</div>
          <div className="mt-2 text-xs">{t("po.currentStage")}: <span className="font-mono">{full.current_stage}</span></div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(full.size_breakdown || {}).map(([sz, q]) => (
          <div key={sz} className="border p-3" style={{ borderColor: "var(--nylsu-border)" }}>
            <div className="text-xs text-[#8F8D84]">{sz}</div>
            <div className="font-mono text-xl">{q}</div>
          </div>
        ))}
      </div>
      <Link to={`/production/orders/${full.id}`} className="text-xs uppercase tracking-[0.2em] link-underline block">
        {t("wip.openFullDetail")}
      </Link>
      {can("production_stage:update") && (
        <div className="pt-4 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
          <div className="data-label mb-3">{t("po.advanceStage")}</div>
          <AdvanceStageForm po={full} onSaved={onAdvanced} />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MATERIAL REQUIREMENTS
═══════════════════════════════════════════════════════════════════ */
export function MaterialRequirementsPage() {
  const { t } = useI18n();
  const [rows] = useList("/material-requirements");
  return (
    <div data-testid="material-requirements-page">
      <PageHeader eyebrow={t("matreq.eyebrow")} title={t("matreq.title")} subtitle={t("matreq.subtitle")} />
      <SectionCard>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("common.code")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
              <th className="data-label px-6 py-3 text-right">{t("po.requiredQty")}</th>
              <th className="data-label px-6 py-3 text-right">{t("po.availableQty")}</th>
              <th className="data-label px-6 py-3 text-right">{t("po.shortage")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.supplier")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.leadTime")}</th>
              <th className="data-label px-6 py-3 text-right">{t("matreq.affectedPOs")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={9} text={t("matreq.empty")} />}
            {rows.map((r) => (
              <tr key={r.material_code} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}
                data-testid={`matreq-row-${r.material_code}`}>
                <td className="px-6 font-mono text-xs">{r.material_code}</td>
                <td className="px-6 text-xs">{r.material_name}</td>
                <td className="px-6 text-right font-mono">{Number(r.required).toFixed(2)} {r.unit}</td>
                <td className="px-6 text-right font-mono">{Number(r.available).toFixed(2)}</td>
                <td className="px-6 text-right font-mono" style={{ color: r.shortage > 0 ? "var(--nylsu-danger)" : "var(--nylsu-success)" }}>
                  {Number(r.shortage).toFixed(2)}
                </td>
                <td className="px-6 text-xs">{r.supplier?.name || "—"}</td>
                <td className="px-6 text-right text-xs">{r.lead_time_days ? `${r.lead_time_days}d` : "—"}</td>
                <td className="px-6 text-right font-mono text-xs">{(r.pos || []).length}</td>
                <td className="px-6"><StatusPill status={r.status.toUpperCase()} tone={r.status === "red" ? "danger" : "success"} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   QC LOG
═══════════════════════════════════════════════════════════════════ */
export function QCPage() {
  const { t, fmtDateTime } = useI18n();
  const [rows] = useList("/qc-records");
  return (
    <div data-testid="qc-page">
      <PageHeader eyebrow={t("qc.eyebrow")} title={t("qc.title")} subtitle={t("qc.subtitle")} />
      <SectionCard>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("common.timestamp")}</th>
              <th className="data-label px-6 py-3 text-left">{t("po.number")}</th>
              <th className="data-label px-6 py-3 text-left">SKU</th>
              <th className="data-label px-6 py-3 text-right">{t("qc.inspected")}</th>
              <th className="data-label px-6 py-3 text-right">{t("qc.passed")}</th>
              <th className="data-label px-6 py-3 text-right">{t("qc.failed")}</th>
              <th className="data-label px-6 py-3 text-left">{t("qc.failureReason")}</th>
              <th className="data-label px-6 py-3 text-left">{t("qc.disposition")}</th>
              <th className="data-label px-6 py-3 text-left">{t("qc.inspector")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={9} text={t("qc.empty")} />}
            {rows.map((r) => (
              <tr key={r.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`qc-row-${r.id}`}>
                <td className="px-6 font-mono text-xs">{fmtDateTime(r.created_at)}</td>
                <td className="px-6 font-mono text-xs">
                  <Link to={`/production/orders/${r.po_id}`} className="link-underline">{r.po_number}</Link>
                </td>
                <td className="px-6 font-mono text-xs">{r.sku}</td>
                <td className="px-6 text-right font-mono">{r.quantity_inspected}</td>
                <td className="px-6 text-right font-mono" style={{ color: "var(--nylsu-success)" }}>{r.quantity_passed}</td>
                <td className="px-6 text-right font-mono" style={{ color: "var(--nylsu-danger)" }}>{r.quantity_failed}</td>
                <td className="px-6 text-xs">{r.failure_reason || "—"}</td>
                <td className="px-6 text-xs">{r.disposition || "—"}</td>
                <td className="px-6 text-xs">{r.inspector_email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   DASHBOARDS
═══════════════════════════════════════════════════════════════════ */
function POMaterialThumb({ materialId, name }) {
  const [prim, setPrim] = useState(null);
  useEffect(() => {
    if (!materialId) return;
    fetchPrimaryAttachments("material", [materialId]).then((list) => setPrim(bulkPrimaryMap(list)[materialId]));
  }, [materialId]);
  return <Thumb attachment={prim} size={40} alt={name || ""} />;
}

function StatCard({ label, value, tone, testId }) {
  return (
    <div className="border p-6" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid={testId}>
      <div className="data-label">{label}</div>
      <div className="font-serif text-4xl mt-4 font-light" style={{ color: tone }}>{value ?? "—"}</div>
    </div>
  );
}

export function OrdersDashboardPage() {
  const { t } = useI18n();
  const [data] = useList("/dashboard/orders");
  const k = data?.kpis || {};
  return (
    <div data-testid="orders-dashboard-page">
      <PageHeader eyebrow={t("orders.dashboardEyebrow")} title={t("orders.dashboardTitle")} subtitle={t("orders.dashboardSub")} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <StatCard label={t("orders.kpi.new")} value={k.new} testId="kpi-new" />
        <StatCard label={t("orders.kpi.productionRequired")} value={k.production_required} tone="var(--nylsu-warning)" testId="kpi-prod-required" />
        <StatCard label={t("orders.kpi.inProduction")} value={k.in_production} testId="kpi-in-prod" />
        <StatCard label={t("orders.kpi.ready")} value={k.ready} tone="var(--nylsu-success)" testId="kpi-ready" />
        <StatCard label={t("orders.kpi.late")} value={k.late} tone="var(--nylsu-danger)" testId="kpi-late" />
        <StatCard label={t("orders.kpi.vipUrgent")} value={k.vip_urgent} tone="var(--nylsu-gold)" testId="kpi-vip" />
      </div>
      {data?.late?.length > 0 && (
        <SectionCard>
          <div className="p-6">
            <div className="overline mb-3">{t("orders.lateList")}</div>
            <table className="w-full text-sm">
              <tbody>
                {data.late.map((o) => (
                  <tr key={o.id} className="border-t" style={{ borderColor: "var(--nylsu-border)" }}>
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link to={`/orders/${o.id}`} className="link-underline">{o.order_number}</Link>
                    </td>
                    <td className="px-4 py-3 text-xs">{o.status}</td>
                    <td className="px-4 py-3 text-xs text-right">{o.required_delivery_date}</td>
                    <td className="px-4 py-3 text-xs text-right" style={{ color: "var(--nylsu-danger)" }}>
                      {Math.abs(o.days_remaining)} {t("common.days")} {t("phase2.late")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

export function ProductionDashboardPage() {
  const { t } = useI18n();
  const [data] = useList("/dashboard/production");
  const k = data?.kpis || {};
  return (
    <div data-testid="production-dashboard-page">
      <PageHeader eyebrow={t("prod.dashboardEyebrow")} title={t("prod.dashboardTitle")} subtitle={t("prod.dashboardSub")} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <StatCard label={t("prod.kpi.openPOs")} value={k.open_pos} testId="kpi-open-pos" />
        <StatCard label={t("prod.kpi.unitsInProduction")} value={k.units_in_production} testId="kpi-units" />
        <StatCard label={t("prod.kpi.completedToday")} value={k.completed_today} tone="var(--nylsu-success)" testId="kpi-completed" />
        <StatCard label={t("prod.kpi.late")} value={k.late} tone="var(--nylsu-danger)" testId="kpi-prod-late" />
        <StatCard label={t("prod.kpi.atRisk")} value={k.at_risk} tone="var(--nylsu-warning)" testId="kpi-atrisk" />
        <StatCard label={t("prod.kpi.materialShortages")} value={k.material_shortages} tone="var(--nylsu-danger)" testId="kpi-shortages" />
        <StatCard label={t("prod.kpi.qcFailures")} value={k.qc_failures} tone="var(--nylsu-danger)" testId="kpi-qc-fail" />
      </div>
      {data?.by_stage?.length > 0 && (
        <SectionCard>
          <div className="p-6">
            <div className="overline mb-3">{t("prod.byStage")}</div>
            <div className="space-y-3">
              {data.by_stage.map((r) => (
                <div key={r._id} className="flex items-center justify-between">
                  <div className="font-serif">{r._id}</div>
                  <div className="font-mono text-xs">{r.count} · {r.units} {t("po.units")}</div>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
